import React, { useRef, useState } from 'react';
import { X, ImagePlus, Palette } from 'lucide-react';
import { uploadLookTexture, fileUrl } from '../lib/api';

// Per-toggle colour / texture swaps on a material (part surface) while the toggle is on.
export default function SwapEditor({ item, swaps, onChange, index }) {
  const materials = item.materials || [];
  const used = new Set(swaps.map((s) => s.material));
  const free = materials.filter((m) => !used.has(m));
  const patch = (i, p) => onChange(swaps.map((s, j) => (j === i ? { ...s, ...p } : s)));
  return (
    <div className="space-y-1.5" data-testid={`looks-swaps-${index}`}>
      <div className="font-osd text-[11px] tracking-widest text-[#ffc968]">CHANGE COLOUR / TEXTURE WHEN ON</div>
      {materials.length === 0 && <div className="font-osd text-[10px] text-[#8b6a3e]">NO SURFACE NAMES FOUND IN THIS FILE.</div>}
      {swaps.map((s, i) => (
        <SwapRow key={s.material} s={s} itemId={item.id} testId={`looks-swap-${index}-${i}`}
                 onPatch={(p) => patch(i, p)} onRemove={() => onChange(swaps.filter((_, j) => j !== i))} />
      ))}
      {free.length > 0 && (
        <select value="" onChange={(e) => e.target.value && onChange([...swaps, { material: e.target.value, color: '#ff3b3b', texture: null }])}
                aria-label="Add a surface to recolour" data-testid={`looks-swap-add-${index}`}
                className="h-8 max-w-full bg-[#1a0e05] border border-dashed border-[#5a3418] text-[#d8b887] font-osd text-[11px] px-2 rounded">
          <option value="">+ ADD SURFACE…</option>
          {free.map((m) => <option key={m} value={m}>{m}</option>)}
        </select>
      )}
    </div>
  );
}

function SwapRow({ s, itemId, onPatch, onRemove, testId }) {
  const ref = useRef(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);
  const pick = async (e) => {
    const f = e.target.files?.[0];
    e.target.value = '';
    if (!f) return;
    setBusy(true); setErr(null);
    try { onPatch({ texture: await uploadLookTexture(itemId, f) }); } catch (x) { setErr(x?.response?.data?.detail || 'Upload failed'); }
    setBusy(false);
  };
  return (
    <div className="flex items-center gap-2 flex-wrap p-2 border border-[#3a2512] rounded bg-[#1a0e05]" data-testid={testId}>
      <span className="font-osd text-[11px] text-[#ffe9b8] truncate max-w-[140px]" title={s.material}>{s.material}</span>
      <label className="flex items-center gap-1 font-osd text-[10px] text-[#8b6a3e]">
        <input type="checkbox" checked={!!s.color} onChange={(e) => onPatch({ color: e.target.checked ? '#ff3b3b' : null })} className="w-4 h-4 accent-[#f5a623]" data-testid={`${testId}-color-on`} />
        <Palette className="w-3.5 h-3.5" />
      </label>
      {s.color && <input type="color" value={s.color} onChange={(e) => onPatch({ color: e.target.value })} className="w-9 h-8 bg-transparent border border-[#3a2512] rounded cursor-pointer" data-testid={`${testId}-color`} />}
      {s.texture ? (
        <span className="inline-flex items-center gap-1">
          <img src={fileUrl(s.texture)} alt="" className="w-8 h-8 object-cover rounded border border-[#3a2512]" />
          <button type="button" onClick={() => onPatch({ texture: null })} aria-label="Remove texture" className="w-6 h-6 flex items-center justify-center text-[#ff8a5c]" data-testid={`${testId}-texture-remove`}><X className="w-3.5 h-3.5" /></button>
        </span>
      ) : (
        <button type="button" onClick={() => ref.current?.click()} disabled={busy} data-testid={`${testId}-texture-add`}
                className="h-8 px-2 inline-flex items-center gap-1 border border-[#5a3418] text-[#ffc968] rounded font-osd text-[10px] tracking-wider hover:border-[#f5a623]">
          <ImagePlus className="w-3.5 h-3.5" /> {busy ? 'UPLOADING…' : 'TEXTURE'}
        </button>
      )}
      <input ref={ref} type="file" accept=".png,.jpg,.jpeg,image/png,image/jpeg" hidden onChange={pick} data-testid={`${testId}-texture-input`} />
      <button type="button" onClick={onRemove} aria-label={`Remove ${s.material}`} className="ml-auto w-7 h-7 flex items-center justify-center text-[#ff8a5c] hover:bg-[#2a1608] rounded" data-testid={`${testId}-remove`}><X className="w-4 h-4" /></button>
      {err && <span className="w-full font-osd text-[10px] text-[#ff6a3a]">{err}</span>}
      {!s.color && !s.texture && <span className="w-full font-osd text-[10px] text-[#ff8a5c]" data-testid={`${testId}-empty`}>PICK A COLOUR OR TEXTURE, OR THIS ROW WON'T BE SAVED</span>}
    </div>
  );
}

